import IvrAudiosAPI from 'dashboard/api/ivrAudios';
import IvrVoicesAPI from 'dashboard/api/ivrVoices';

export const state = {
  audios: [],
  voices: [],
  models: [],
  subscription: null,
  selectedAudio: null,
  apiKeyStatus: {
    configured: false,
    maskedKey: null,
  },
  meta: {
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
    perPage: 25,
  },
  uiFlags: {
    isFetching: false,
    isFetchingVoices: false,
    isFetchingModels: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isUploading: false,
    isGeneratingPreview: false,
    isTestingApiKey: false,
    isSavingApiKey: false,
  },
  filters: {
    sourceType: '',
    status: '',
  },
};

export const getters = {
  getAudios: $state => $state.audios,
  getVoices: $state => $state.voices,
  getModels: $state => $state.models,
  getSubscription: $state => $state.subscription,
  getSelectedAudio: $state => $state.selectedAudio,
  getApiKeyStatus: $state => $state.apiKeyStatus,
  getMeta: $state => $state.meta,
  getUIFlags: $state => $state.uiFlags,
  getFilters: $state => $state.filters,
  getById: $state => id => $state.audios.find(a => a.id === id),
  getTtsAudios: $state => $state.audios.filter(a => a.source_type === 'tts'),
  getUploadedAudios: $state =>
    $state.audios.filter(a => a.source_type === 'upload'),
  getReadyAudios: $state => $state.audios.filter(a => a.status === 'ready'),
  getPendingAudios: $state =>
    $state.audios.filter(
      a => a.status === 'pending' || a.status === 'processing'
    ),
  isApiKeyConfigured: $state => $state.apiKeyStatus.configured,
};

export const actions = {
  // Fetch all IVR audios
  async fetch({ commit, state: $state }, { page = 1, resetList = false, platform } = {}) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const params = {
        page,
        source_type: $state.filters.sourceType || undefined,
        status: $state.filters.status || undefined,
      };

      if (platform) params.platform = platform;

      Object.keys(params).forEach(
        key => params[key] === undefined && delete params[key]
      );

      const response = await IvrAudiosAPI.get(params);
      const { payload, meta } = response.data;

      if (resetList || page === 1) {
        commit('SET_AUDIOS', payload);
      } else {
        commit('APPEND_AUDIOS', payload);
      }
      commit('SET_META', meta);

      return payload;
    } catch (error) {
      console.error('[ivrAudios] Error fetching:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  // Fetch a single audio
  async fetchById({ commit }, id) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await IvrAudiosAPI.show(id);
      commit('SET_SELECTED_AUDIO', response.data);
      return response.data;
    } catch (error) {
      console.error('[ivrAudios] Error fetching audio:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  // Create new TTS audio
  async create({ commit }, audioData) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const response = await IvrAudiosAPI.create(audioData);
      commit('ADD_AUDIO', response.data);
      return response.data;
    } catch (error) {
      console.error('[ivrAudios] Error creating:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  // Update audio
  async update({ commit }, { id, ...data }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await IvrAudiosAPI.update(id, data);
      commit('UPDATE_AUDIO', response.data);
      return response.data;
    } catch (error) {
      console.error('[ivrAudios] Error updating:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  // Delete audio
  async delete({ commit }, id) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await IvrAudiosAPI.delete(id);
      commit('DELETE_AUDIO', id);
    } catch (error) {
      console.error('[ivrAudios] Error deleting:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  // Regenerate TTS audio
  async regenerate({ commit }, id) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await IvrAudiosAPI.regenerate(id);
      commit('UPDATE_AUDIO', response.data);
      return response.data;
    } catch (error) {
      console.error('[ivrAudios] Error regenerating:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  // Upload audio file
  async upload({ commit }, { file, name, description, platform }) {
    commit('SET_UI_FLAG', { isUploading: true });
    try {
      const formData = new FormData();
      formData.append('audio_file', file);
      formData.append('name', name);
      if (description) {
        formData.append('description', description);
      }
      if (platform) {
        formData.append('platform', platform);
      }

      const response = await IvrAudiosAPI.upload(formData);
      commit('ADD_AUDIO', response.data);
      return response.data;
    } catch (error) {
      console.error('[ivrAudios] Error uploading:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isUploading: false });
    }
  },

  // Fetch available voices from ElevenLabs
  async fetchVoices({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetchingVoices: true });
    try {
      const response = await IvrVoicesAPI.get(params);
      commit('SET_VOICES', response.data.voices);
      return response.data.voices;
    } catch (error) {
      console.error('[ivrAudios] Error fetching voices:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetchingVoices: false });
    }
  },

  // Fetch available models
  async fetchModels({ commit }) {
    commit('SET_UI_FLAG', { isFetchingModels: true });
    try {
      const response = await IvrVoicesAPI.getModels();
      commit('SET_MODELS', response.data.models);
      return response.data.models;
    } catch (error) {
      console.error('[ivrAudios] Error fetching models:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetchingModels: false });
    }
  },

  // Fetch subscription info
  async fetchSubscription({ commit }) {
    try {
      const response = await IvrVoicesAPI.getSubscription();
      commit('SET_SUBSCRIPTION', response.data.subscription);
      return response.data.subscription;
    } catch (error) {
      console.error('[ivrAudios] Error fetching subscription:', error);
      throw error;
    }
  },

  // Preview voice (returns audio blob URL)
  async previewVoice({ commit }, { voiceId, text, modelId, voiceSettings }) {
    commit('SET_UI_FLAG', { isGeneratingPreview: true });
    try {
      const response = await IvrVoicesAPI.preview({
        voice_id: voiceId,
        text,
        model_id: modelId,
        voice_settings: voiceSettings,
      });

      // Create blob URL for playback
      const blob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(blob);
      return audioUrl;
    } catch (error) {
      console.error('[ivrAudios] Error previewing voice:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isGeneratingPreview: false });
    }
  },

  // Update from WebSocket event
  updateFromSocket({ commit, state: $state }, audioData) {
    // Check if audio exists in our list
    const exists = $state.audios.some(a => a.id === audioData.id);
    if (exists) {
      commit('UPDATE_AUDIO', audioData);
    } else {
      // If it's a new audio for this account, add it
      commit('ADD_AUDIO', audioData);
    }
  },

  // Update audio from WebSocket (called by ActionCable)
  updateAudioFromWebsocket({ commit, state: $state }, audioData) {
    const exists = $state.audios.some(a => a.id === audioData.id);
    if (exists) {
      commit('UPDATE_AUDIO', audioData);
    }
  },

  // Set filters
  setFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters);
    return dispatch('fetch', { page: 1, resetList: true });
  },

  // Clear filters
  clearFilters({ commit, dispatch }) {
    commit('CLEAR_FILTERS');
    return dispatch('fetch', { page: 1, resetList: true });
  },

  // Set selected audio
  setSelectedAudio({ commit }, audio) {
    commit('SET_SELECTED_AUDIO', audio);
  },

  // Clear selected audio
  clearSelectedAudio({ commit }) {
    commit('SET_SELECTED_AUDIO', null);
  },

  // Fetch API key status
  async fetchApiKeyStatus({ commit }) {
    try {
      const response = await IvrVoicesAPI.getApiKeyStatus();
      commit('SET_API_KEY_STATUS', response.data);
      return response.data;
    } catch (error) {
      console.error('[ivrAudios] Error fetching API key status:', error);
      commit('SET_API_KEY_STATUS', { configured: false, masked_key: null });
      throw error;
    }
  },

  // Test API key
  async testApiKey({ commit }, apiKey) {
    commit('SET_UI_FLAG', { isTestingApiKey: true });
    try {
      const response = await IvrVoicesAPI.testApiKey(apiKey);
      return response.data;
    } catch (error) {
      console.error('[ivrAudios] Error testing API key:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isTestingApiKey: false });
    }
  },

  // Save API key
  async saveApiKey({ commit }, apiKey) {
    commit('SET_UI_FLAG', { isSavingApiKey: true });
    try {
      const response = await IvrVoicesAPI.saveApiKey(apiKey);
      if (response.data.success) {
        commit('SET_API_KEY_STATUS', {
          configured: apiKey !== '',
          masked_key: response.data.masked_key,
        });
      }
      return response.data;
    } catch (error) {
      console.error('[ivrAudios] Error saving API key:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isSavingApiKey: false });
    }
  },
};

export const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  SET_AUDIOS($state, audios) {
    $state.audios = audios;
  },

  APPEND_AUDIOS($state, audios) {
    $state.audios = [...$state.audios, ...audios];
  },

  ADD_AUDIO($state, audio) {
    $state.audios = [audio, ...$state.audios];
    $state.meta.totalCount += 1;
  },

  UPDATE_AUDIO($state, audio) {
    const index = $state.audios.findIndex(a => a.id === audio.id);
    if (index !== -1) {
      // Create new array reference to ensure Vue reactivity with TanStack Table
      $state.audios = $state.audios.map(a => (a.id === audio.id ? audio : a));
    }
    if ($state.selectedAudio?.id === audio.id) {
      $state.selectedAudio = audio;
    }
  },

  DELETE_AUDIO($state, id) {
    $state.audios = $state.audios.filter(a => a.id !== id);
    if ($state.meta.totalCount > 0) {
      $state.meta.totalCount -= 1;
    }
    if ($state.selectedAudio?.id === id) {
      $state.selectedAudio = null;
    }
  },

  SET_META($state, meta) {
    $state.meta = {
      totalCount: meta.total_count,
      currentPage: meta.current_page,
      totalPages: meta.total_pages,
      perPage: meta.per_page,
    };
  },

  SET_VOICES($state, voices) {
    $state.voices = voices;
  },

  SET_MODELS($state, models) {
    $state.models = models;
  },

  SET_SUBSCRIPTION($state, subscription) {
    $state.subscription = subscription;
  },

  SET_SELECTED_AUDIO($state, audio) {
    $state.selectedAudio = audio;
  },

  SET_FILTERS($state, filters) {
    $state.filters = { ...$state.filters, ...filters };
  },

  CLEAR_FILTERS($state) {
    $state.filters = {
      sourceType: '',
      status: '',
    };
  },

  SET_API_KEY_STATUS($state, status) {
    $state.apiKeyStatus = {
      configured: status.configured,
      maskedKey: status.masked_key,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
